
/**
 * Changes made:
 * - 2025-06-14: Removed DealerSignup, Dealers, DealerDashboard, About, Contact, Sellers, SellMyCar, and Terms pages and their routes
 * - 2025-05-25: Implemented proper routing setup with BrowserRouter
 * - 2025-06-22: Removed BrowserRouter as it's now in main.tsx
 * - 2025-06-22: Confirmed all deleted pages and their routes removed
 * - 2025-06-22: Removed /dashboard/car/:carId route (CarDetailsPage)
 * - 2025-06-22: Restored SellMyCar page and route to support post-valuation listing flow
 * - 2025-10-09: Added Google Ads tracking integration for page views
 * - 2025-10-15: Translated all loading indicators from English to Polish
 * - 2025-11-23: Added password reset routes for sellers
 * - 2026-02-26: Fully removed /vin-i-win route (no redirect, falls to 404)
 * - 2026-03-07: Added campaign tracking capture hook (useTrackingCapture)
 */

import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { clearStaleLocalStorage } from './config/storage';
import { pageview } from './utils/gtag';
import { useTrackingCapture } from '@/hooks/useTrackingCapture';
import IndexPage from './pages/Index';
import { lazy, Suspense } from 'react';
import { CookieConsent } from './components/CookieConsent';

// Lazy load all pages for better performance
const HowItWorksPage = lazy(() => import('./pages/HowItWorks'));
const ManualValuationPage = lazy(() => import('./pages/ManualValuation'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));
const PrivacyPage = lazy(() => import('./pages/Privacy'));
const AuthPage = lazy(() => import('./pages/Auth'));
const SellerDashboardPage = lazy(() => import('./pages/SellerDashboard'));
// Removed: const CarDetailsPage = lazy(() => import('./pages/CarDetails'));
const SellMyCarPage = lazy(() => import('./pages/SellMyCar'));

const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'));
const UpdatePasswordPage = lazy(() => import('./pages/UpdatePassword'));

function App() {
  const location = useLocation();
  useTrackingCapture(); // Campaign attribution capture on app load

  useEffect(() => {
    // Clear any stale local storage references on app initialization
    clearStaleLocalStorage();
  }, []);

  // Track page views on route change
  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  return (
    <>
      <CookieConsent />
      <Routes>
        <Route path="/" element={<IndexPage />} />
        <Route path="/how-it-works" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <HowItWorksPage />
          </Suspense>
        } />
        <Route path="/manual-valuation" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <ManualValuationPage />
          </Suspense>
        } />
        <Route path="/privacy" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <PrivacyPage />
          </Suspense>
        } />
        <Route path="/auth" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <AuthPage />
          </Suspense>
        } />
        <Route path="/dashboard/seller" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <SellerDashboardPage />
          </Suspense>
        } />
        <Route path="/sell-my-car" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <SellMyCarPage />
          </Suspense>
        } />
        
        <Route path="/auth/reset-password" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <ResetPasswordPage />
          </Suspense>
        } />
        <Route path="/auth/update-password" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <UpdatePasswordPage />
          </Suspense>
        } />
        {/* Removed: <Route path="/dashboard/car/:carId" element={<Suspense>...</Suspense>} /> */}
        {/* 404 page for any unmatched routes */}
        <Route path="*" element={
          <Suspense fallback={<div>Ładowanie...</div>}>
            <NotFoundPage />
          </Suspense>
        } />
      </Routes>
    </>
  );
}

export default App;
